import React, { useCallback, useEffect, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { connect } from 'react-redux';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import 'moment-timezone';

import EventContainer from './EventContainer';
import ToolbarContainer from './ToolbarContainer';
import Alert from '../../../components/Alert';
import { can } from '../../../components/Authorize';
import { setEventForm, fetchEvents, fetchCurrentEvent } from '../../../store/actions/events';
import { deleteEvent } from '../../../helpers/api';
import { dateTimeFormat } from '../../../helpers/constants';
import { EVENT_WRITE } from '../../../components/Authorize/permissions/resources';
import { DAY_CLICK_CREATE_EVENT } from '../permissions';

import '../../../vendor/libs/react-big-calendar/react-big-calendar.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.scss';
import style from './style.module.scss';

const localizer = momentLocalizer(moment);
const formats = {
  dateFormat: 'D',
  weekdayFormat: (date, culture) => localizer.format(date, 'ddd', culture),
};

const CalendarContainer = (props) => {
  const {
    allEvents,
    timezone,
    roles,
    setEventFormAction,
    fetchEventsAction,
    fetchCurrentEventAction,
    isAdmin,
  } = props;

  useEffect(() => {
    fetchEventsAction().catch((err) => {
      console.error('Error fetching events', err);
    });
  }, []);

  const canCreateEvent = useMemo(() => can([EVENT_WRITE, DAY_CLICK_CREATE_EVENT], true), [roles]);

  const onDaySelectedHandler = useCallback(async (event) => {
    if (!canCreateEvent) {
      return;
    }

    const eventObj = { ...event };
    eventObj.className = 'upcoming-event';
    const startDate = moment(event.start).format(dateTimeFormat);
    const endDate = moment(event.end).format(dateTimeFormat);
    const now = moment().tz(timezone).format(dateTimeFormat);
    const isStartInPast = moment(startDate).diff(now, 'd') < 0;
    const isEndInPast = moment(endDate).diff(now, 'd') < 0;

    if (isStartInPast || isEndInPast) {
      await Alert.fire({
        title: 'Past Event',
        text: 'You cannot create events in the past',
        confirmButtonText: 'Got it!',
        customClass: {
          confirmButton: 'small btn btn-primary',
          content: 'text-nowrap',
        },
      });
      return;
    }

    const eventsSameDay = allEvents.some((savedEvent) => {
      const saveEventDate = moment(savedEvent.start).startOf('day');
      const repeatedEvents = event.slots.some((newEvent) => (
        moment(newEvent).startOf('day').diff(saveEventDate.startOf('day'), 'day') === 0));
      return repeatedEvents;
    });

    if (eventsSameDay) {
      await Alert.fire({
        title: 'Attention',
        text: 'Please, check your selection, you cannot create events on days that you already have events',
      });
      return;
    }

    setEventFormAction({ selectedSlots: event.slots, fromDate: event.start, toDate: event.end });
  }, [canCreateEvent, allEvents]);

  const editButtonHandler = useCallback(async (selectedEvent) => {
    const {
      title,
      start: gameDate,
      startTimeObject,
      endTimeObject,
      slugDate,
    } = allEvents.find((ev) => ev.slugDate === selectedEvent.slugDate);

    const gameDateF = moment.tz(gameDate, timezone).toDate();

    const eventObj = {
      fromDate: gameDateF,
      startTime: new Date(moment(startTimeObject).tz(timezone).format(dateTimeFormat)),
      endTime: new Date(moment(endTimeObject).tz(timezone).format(dateTimeFormat)),
      slugDate,
      eventTitle: title,
      formMode: 'update',
      // isNextDay should be checked if the difference between startTime and endTime
      // exceeds 1 day based on the event's timezone, not the user's local.
      isNextDay: startTimeObject && endTimeObject
        ? moment.tz(endTimeObject, timezone).startOf('day')
          .diff(moment.tz(startTimeObject, timezone).startOf('day'), 'days') > 0
        : false,
    };

    setEventFormAction(eventObj);
  }, [allEvents, timezone]);

  const deleteButtonHandler = useCallback(async (e, selectedEvent) => {
    e.preventDefault();
    let slugDateInput = '';

    await Alert.fire({
      title: 'Do you want to delete the event?',
      type: 'question',
      html: (
        <div className="text-left">
          <div>
            Type the slug date to confirm deletion:
            {' '}
            <b>{selectedEvent.slugDate}</b>
            .
          </div>
          <input
            type="text"
            className="form-control mt-2"
            onChange={(evt) => {
              slugDateInput = evt.target.value;
            }}
          />
          {isAdmin && (
            <Form.Check
              custom
              type="checkbox"
              id="sendCalendarInviteDelete"
              className="mt-3"
              label="Send Calendar Invite"
              defaultChecked
            />
          )}
        </div>
      ),
      showCancelButton: true,
      showLoaderOnConfirm: true,
      allowOutsideClick: false,
      preConfirm: async () => {
        if (slugDateInput !== selectedEvent.slugDate) {
          Alert.showValidationMessage('Please, enter the correct slug date');
          return false;
        }
        const inviteCheckbox = document.getElementById('sendCalendarInviteDelete');
        const invite = inviteCheckbox ? inviteCheckbox.checked : true;
        try {
          await deleteEvent(selectedEvent.slugDate, invite);
          await fetchEventsAction();
          await fetchCurrentEventAction();
          await Alert.fire({ title: 'Event has been deleted' });
          return true;
        } catch (err) {
          console.error('Error trying to delete the event from database', err);
          Alert.showValidationMessage('Error trying to delete event from database. Please, try again or contact support!');
          return false;
        }
      },
    });
  }, [isAdmin]);

  return (
    <Calendar
      className={style.calendarContainer}
      style={{ minHeight: '80vh', maxHeight: '90vh' }}
      events={allEvents}
      formats={formats}
      popup
      selectable={canCreateEvent}
      resizable={false}
      defaultDate={new Date(moment.tz(timezone).format('ll'))}
      localizer={localizer}
      eventPropGetter={({ className }) => (className ? { className } : null)}
      onSelectSlot={onDaySelectedHandler}
      components={{
        toolbar: ToolbarContainer,
        event: (componentProps) => (
          <EventContainer
            {...componentProps}
            editEventHandler={editButtonHandler}
            deleteEventHandler={deleteButtonHandler}
          />
        ),
      }}
    />
  );
};

export default connect(
  ({
    events: { allEvents, timezone },
    roles: { roles },
    authentication: { roles: userRoles = [] } = {},
  }) => ({
    allEvents,
    timezone,
    roles,
    isAdmin: userRoles.includes('admin'),
  }),
  {
    setEventFormAction: setEventForm,
    fetchEventsAction: fetchEvents,
    fetchCurrentEventAction: fetchCurrentEvent,
  },
)(CalendarContainer);
